import { FC, useMemo, useState } from "react";
import {
	Box,
	Checkbox,
	Chip,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";

import ConfigEditorShell from "../../components/ConfigEditorShell/ConfigEditorShell";
import {
	useConfigEditor,
	type ExitRoute,
} from "../../components/ConfigEditorShell/useConfigEditor";
import { IAnalyte } from "../../domain/analyte.interface";
import { CupResponse, CupStatus } from "../../domain/cup.interface";
import {
	useCreateCup,
	useInUseVendors,
	useUpdateCup,
} from "../../hooks/useCups";
import { useUserStore } from "../../stores/user.store";
import { useFeedBack } from "../../providers/FeedBackProvider/FeedBackContext";
import {
	canSaveCup,
	cupDraftFrom,
	duplicateNameMessage,
	emptyCupDraft,
	filterVendorSuggestions,
	toCupPayload,
	vendorCountLabel,
	vendorDisplayPlaceholder,
	type CupDraft,
} from "./cupDraft";

/**
 * SIMS-8799 — the cup editor.
 *
 * The chrome is Foundation's: `ConfigEditorShell` + `useConfigEditor` give the breadcrumb, the
 * sticky Cancel/Save header, the "Unsaved changes" pill, the discard prompt and the save-failed
 * banner, so states cups-11 through cups-14 need no code here. `src/pages/Payers/PayerEditor.tsx`
 * is the worked example.
 *
 * AC5 — THE EDITOR NEVER WRITES THE DISPLAY NAME. The Hi-Fi does, twice (markup 1168/1170 on save
 * and 1384 on picking a suggestion), but SIMS-8962 AC6 shipped a read-time fallback with the stored
 * value left empty, and TestRail C17933 asserts that at the database level. See `cupDraft.ts`.
 */
export interface CupEditorProps {
	/** Undefined for a new cup. */
	cup?: CupResponse;
	/** The analyte catalogue, already loaded by the screen for its Analytes tab. */
	analytes: IAnalyte[];
	/** The catalogue query is still in flight — the checklist is unknown, not empty. */
	analytesLoading?: boolean;
	/** The catalogue query failed — same distinction, different message. */
	analytesError?: boolean;
	onExit: () => void;
}

const CupEditor: FC<CupEditorProps> = ({
	cup,
	analytes,
	analytesLoading = false,
	analytesError = false,
	onExit,
}) => {
	const isNew = !cup;
	const { userInfo } = useUserStore();
	const { showSnackBar } = useFeedBack();
	const createCup = useCreateCup();
	const updateCup = useUpdateCup();
	const { data: vendorData } = useInUseVendors();
	const vendors = useMemo(() => vendorData?.body ?? [], [vendorData]);

	const [vendorSuggestOpen, setVendorSuggestOpen] = useState(false);
	// -1 = nothing highlighted, which is the state a mouse user stays in.
	const [activeSuggestion, setActiveSuggestion] = useState(-1);
	const [analyteQuery, setAnalyteQuery] = useState("");

	const editor = useConfigEditor<CupDraft>({
		initial: cup ? cupDraftFrom(cup) : emptyCupDraft(),
		fallbackSaveError: "The cup could not be saved.",
		onSave: async (draft) => {
			const payload = toCupPayload(
				draft,
				process.env.REACT_APP_ACCOUNT_ID || "",
				userInfo?.id || "SYSTEM_USER",
			);
			if (cup) {
				// The update contract swaps createdBy for updatedBy.
				const { createdBy, ...rest } = payload;
				await updateCup.mutateAsync({
					...rest,
					_id: cup._id,
					updatedBy: createdBy,
				});
			} else {
				await createCup.mutateAsync(payload);
			}
			// SUCCESS only, per the house rule Payers/index.tsx:596-598 records: a failure shows the
			// shell's persistent banner instead, never a toast. ConfigEditorShell carries no
			// Snackbar of its own, so this is the editor's to raise.
			showSnackBar(
				cup ? "Cup updated successfully" : "Cup created successfully",
				"success",
			);
			onExit();
		},
	});

	const { draft, setField } = editor;
	const leave = (route: ExitRoute): void => editor.requestExit(onExit, route);

	// AC3 — a 409 from the uniqueness index (SIMS-8962 AC4) is the name being taken, not a generic
	// failure. Everything else falls through to the shell's own message.
	const conflict = duplicateNameMessage(
		(createCup.error ?? updateCup.error) as unknown,
	);

	const suggestions = useMemo(
		() => filterVendorSuggestions(draft.vendor, vendors),
		[draft.vendor, vendors],
	);

	/**
	 * AC5 — sets the VENDOR only. The Hi-Fi also writes the display name here (markup 1384); that
	 * is what C17933 forbids. Shared by the mouse and keyboard paths so they cannot drift.
	 */
	const pickVendor = (vendor: string): void => {
		setField("vendor", vendor);
		setVendorSuggestOpen(false);
		setActiveSuggestion(-1);
	};

	/**
	 * The panel was reachable by mouse only: onBlur closes it, so tabbing dismissed the list before
	 * focus could land on an option. Arrow keys move the highlight without moving focus, which is
	 * why the aria-activedescendant pattern exists.
	 */
	const onVendorKeyDown = (e: React.KeyboardEvent): void => {
		if (e.key === "Escape") {
			setVendorSuggestOpen(false);
			setActiveSuggestion(-1);
			return;
		}
		if (suggestions.length === 0) return;
		if (e.key === "ArrowDown" || e.key === "ArrowUp") {
			e.preventDefault();
			if (!vendorSuggestOpen) {
				setVendorSuggestOpen(true);
				setActiveSuggestion(e.key === "ArrowDown" ? 0 : suggestions.length - 1);
				return;
			}
			const step = e.key === "ArrowDown" ? 1 : -1;
			const last = suggestions.length - 1;
			setActiveSuggestion((prev) =>
				prev < 0
					? step === 1
						? 0
						: last
					: (prev + step + suggestions.length) % suggestions.length,
			);
			return;
		}
		// Enter only commits when something is highlighted; otherwise it stays the form's own key,
		// so a user who typed a brand-new vendor (AC2) is not hijacked into an existing one.
		if (e.key === "Enter" && vendorSuggestOpen && activeSuggestion >= 0) {
			e.preventDefault();
			pickVendor(suggestions[activeSuggestion].vendor);
		}
	};

	const choices = useMemo(() => {
		const q = analyteQuery.trim().toLowerCase();
		return q
			? analytes.filter(
					(a) =>
						a.shortname.toLowerCase().includes(q) ||
						a.scientificName.toLowerCase().includes(q),
				)
			: analytes;
	}, [analytes, analyteQuery]);

	const selected = useMemo(
		() => analytes.filter((a) => draft.analytes.includes(a._id)),
		[analytes, draft.analytes],
	);

	const toggleAnalyte = (id: string): void =>
		setField(
			"analytes",
			draft.analytes.includes(id)
				? draft.analytes.filter((a) => a !== id)
				: [...draft.analytes, id],
		);

	return (
		<ConfigEditorShell
			// ANCESTORS ONLY. ConfigEditorShell appends `title` as the leaf itself, so passing a
			// third crumb here rendered "… / New cup / New cup". Matches PayerEditor and both
			// Actions editors.
			breadcrumbs={[
				{ label: "Program setup" },
				{ label: "Cups & analytes", onClick: () => leave("breadcrumb") },
			]}
			title={isNew ? "New cup" : (cup?.name ?? "Cup")}
			isNew={isNew}
			isDirty={editor.isDirty}
			isSaving={editor.isSaving}
			canSave={editor.canSave && canSaveCup(draft)}
			saveError={conflict ?? editor.saveError}
			failedTitle="Cup wasn't saved"
			discardBody="This cup has changes that have not been saved. Discarding closes the editor and leaves the cup as it was."
			discardPromptOpen={editor.discardPromptOpen}
			onSave={editor.save}
			onCancel={() => leave("cancel")}
			onBreadcrumb={() => leave("breadcrumb")}
			onKeepEditing={editor.keepEditing}
			onDiscard={editor.discard}
		>
			<Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
				<Box>
					<Typography sx={{ fontSize: "1.25rem", fontWeight: 700, mb: 2 }}>
						Cup details
					</Typography>
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
							gap: 2,
						}}
					>
						<TextField
							label="Cup name"
							required
							value={draft.name}
							onChange={(e) => setField("name", e.target.value)}
							placeholder="e.g. 12 Panel"
						/>

						{/* AC2 — free text with suggestions. The panel is anchored to this cell and
						    overlays the Status select, as the Hi-Fi has it (cups-08). */}
						<Box sx={{ position: "relative" }}>
							<TextField
								fullWidth
								label="Cup vendor"
								required
								value={draft.vendor}
								onChange={(e) => {
									setField("vendor", e.target.value);
									setVendorSuggestOpen(true);
									// The list re-filters under the highlight, so drop it.
									setActiveSuggestion(-1);
								}}
								onFocus={() => setVendorSuggestOpen(true)}
								onBlur={() => {
									setVendorSuggestOpen(false);
									setActiveSuggestion(-1);
								}}
								onKeyDown={onVendorKeyDown}
								placeholder="e.g. CLIA"
								helperText="Free text. Pick an existing vendor where you can — each new spelling becomes its own filter entry."
								inputProps={{
									role: "combobox",
									"aria-expanded": vendorSuggestOpen,
									"aria-controls": "vendor-suggestions",
									"aria-autocomplete": "list",
									"aria-activedescendant":
										vendorSuggestOpen && activeSuggestion >= 0
											? `vendor-suggestion-${activeSuggestion}`
											: undefined,
								}}
							/>
							{vendorSuggestOpen && (
								<Box
									id="vendor-suggestions"
									data-testid="vendor-suggestions"
									role="listbox"
									aria-label="Vendors already in use"
									sx={{
										position: "absolute",
										top: "4.625rem",
										left: 0,
										right: 0,
										zIndex: 30,
										maxHeight: "12.5rem",
										overflowY: "auto",
										backgroundColor: "background.paper",
										border: "1px solid",
										borderColor: "divider",
										borderRadius: "0.5rem",
										boxShadow: 8,
									}}
								>
									{suggestions.length > 0 ? (
										<>
											<Typography
												sx={{
													fontSize: "0.6875rem",
													fontWeight: 600,
													letterSpacing: "0.08em",
													textTransform: "uppercase",
													color: "text.secondary",
													px: 1.5,
													pt: 1.5,
												}}
											>
												Vendors already in use
											</Typography>
											{suggestions.map((v, i) => (
												<Box
													key={v.vendor}
													component="button"
													type="button"
													id={`vendor-suggestion-${i}`}
													role="option"
													aria-selected={i === activeSuggestion}
													// onMouseDown, not onClick: the input's onBlur would close the
													// panel before a click ever landed.
													onMouseDown={(e: React.MouseEvent) => {
														e.preventDefault();
														pickVendor(v.vendor);
													}}
													onMouseEnter={() => setActiveSuggestion(i)}
													sx={{
														display: "flex",
														alignItems: "center",
														gap: 1,
														width: "100%",
														border: "none",
														backgroundColor:
															i === activeSuggestion
																? "action.hover"
																: "transparent",
														cursor: "pointer",
														textAlign: "left",
														p: 1.5,
													}}
												>
													<BusinessIcon fontSize="small" />
													<Typography sx={{ fontSize: "0.875rem", flex: 1 }}>
														{v.vendor}
													</Typography>
													<Typography
														sx={{
															fontSize: "0.75rem",
															color: "text.secondary",
														}}
													>
														{vendorCountLabel(v)}
													</Typography>
												</Box>
											))}
										</>
									) : (
										<Typography
											sx={{
												fontSize: "0.8125rem",
												color: "text.secondary",
												p: 1.5,
											}}
										>
											No vendor in use matches. It will be added as a new
											vendor.
										</Typography>
									)}
								</Box>
							)}
						</Box>

						<TextField
							label="Vendor display name"
							value={draft.display}
							onChange={(e) => setField("display", e.target.value)}
							// AC5 — states the fallback without performing it.
							placeholder={vendorDisplayPlaceholder(draft)}
							helperText="Defaults to the vendor when left blank."
						/>

						<FormControl>
							<InputLabel id="cup-status-label">Status</InputLabel>
							<Select
								labelId="cup-status-label"
								label="Status"
								value={draft.status}
								onChange={(e) =>
									setField("status", e.target.value as CupStatus)
								}
							>
								<MenuItem value={CupStatus.ACTIVE}>Active</MenuItem>
								<MenuItem value={CupStatus.INACTIVE}>Inactive</MenuItem>
							</Select>
						</FormControl>

						{/* Required against the design, which draws no asterisk here. The API refuses a
						    blank description (Cup.description is required; the DTOs are not
						    @IsOptional), so leaving it optional produced a save that always failed.
						    See canSaveCup for the full reasoning and the open Q5. */}
						<TextField
							label="Description"
							required
							value={draft.description}
							onChange={(e) => setField("description", e.target.value)}
							placeholder="e.g. 12 Panel IDTC Cups II with Adulterants"
							multiline
							minRows={2}
							sx={{ gridColumn: { md: "1 / -1" } }}
						/>
					</Box>
				</Box>

				<Box>
					<Typography sx={{ fontSize: "1.25rem", fontWeight: 700, mb: 1 }}>
						Analytes
					</Typography>
					{/* FR-44 — a count of what the cup carries, never the vendor's panel number. */}
					<Typography
						sx={{ fontSize: "0.8125rem", color: "text.secondary", mb: 2 }}
						data-testid="cup-editor-analyte-summary"
					>
						{`${draft.analytes.length} ${
							draft.analytes.length === 1 ? "analyte" : "analytes"
						} on this cup. Validity checks count as analytes, so this is not the vendor's panel number.`}
					</Typography>

					<TextField
						size="small"
						value={analyteQuery}
						onChange={(e) => setAnalyteQuery(e.target.value)}
						placeholder="Filter analytes"
						sx={{ mb: 2, width: { xs: "100%", sm: "20rem" } }}
					/>

					{selected.length > 0 && (
						<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 2 }}>
							{selected.map((a) => (
								<Chip
									key={a._id}
									size="small"
									label={`${a.shortname} — ${a.scientificName}`}
									onDelete={() => toggleAnalyte(a._id)}
								/>
							))}
						</Box>
					)}

					{/* An empty checklist is ambiguous on its own, so each reason says which it is.
					    The element map lists `loading` and `empty` as states of
					    cup-editor-analyte-checklist; `failed` is ours, and it matters more than the
					    other two — a silent empty grid invites saving a cup with no analytes. */}
					{analytesLoading || analytesError || choices.length === 0 ? (
						<Typography
							data-testid="cup-editor-analyte-state"
							sx={{ fontSize: "0.8125rem", color: "text.secondary", py: 2 }}
						>
							{analytesLoading
								? "Loading the analyte catalogue…"
								: analytesError
									? "The analyte catalogue could not be loaded, so none can be selected. Close the editor and try again."
									: analytes.length === 0
										? "No analytes exist yet. Add analytes before building a cup."
										: "No analyte matches that filter."}
						</Typography>
					) : (
						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
								gap: 1,
							}}
						>
							{choices.map((a) => (
								<Box
									key={a._id}
									sx={{ display: "flex", alignItems: "center", gap: 1 }}
								>
									<Checkbox
										size="small"
										checked={draft.analytes.includes(a._id)}
										onChange={() => toggleAnalyte(a._id)}
										inputProps={{ "aria-label": a.shortname }}
									/>
									<Box sx={{ minWidth: 0 }}>
										<Typography
											sx={{ fontSize: "0.8125rem", fontWeight: 600 }}
											noWrap
										>
											{a.shortname}
										</Typography>
										<Typography
											sx={{ fontSize: "0.75rem", color: "text.secondary" }}
											noWrap
										>
											{a.scientificName}
										</Typography>
									</Box>
								</Box>
							))}
						</Box>
					)}
				</Box>
			</Box>
		</ConfigEditorShell>
	);
};

export default CupEditor;
